import { Box } from "@mui/material";
import Head from "next/head";
import { NEWS_IMAGE_FALLBACK_STYLES } from "./newsStyles";
import { useImageLoadFailed } from "./useImageLoadFailed";

interface NewsImageProps {
  /** 언론사 CDN 원본 주소 — 저작권 상 로컬에 저장하지 않고 그대로 참조한다(press-news) */
  src: string;
  /** 원본 CDN 의 같은 사진 크기별 판(폭 서술자) — 있으면 `sizes` 와 함께 화면 폭에 맞는 판 하나만 받는다 */
  srcSet?: string;
  /** 화면에 보이는 폭 */
  sizes?: string;
  alt: string;
  /** 주소가 만료됐을 때 그 자리에 보일 글자 */
  fallbackText: string;
  /** 원본 가로·세로(px) — 주면 사진이 들어오기 전에 자리를 잡는다 */
  width?: number;
  height?: number;
  /** 첫 화면 사진: 페이지를 열자마자 높은 우선순위로 받고 미리 받기 링크를 단다. 아니면 화면 가까이 와야 받는다 */
  priority?: boolean;
}

/** 핫링크 기사 사진. 불러오지 못하면 깨진 아이콘 대신 같은 칸에 대체 글자를 보인다. */
export function NewsImage({
  src,
  srcSet,
  sizes,
  alt,
  fallbackText,
  width,
  height,
  priority = false,
}: NewsImageProps) {
  const { failed, imgRef, onError } = useImageLoadFailed(src);
  if (failed) {
    return (
      <Box role="img" aria-label={alt} sx={NEWS_IMAGE_FALLBACK_STYLES}>
        {fallbackText}
      </Box>
    );
  }
  return (
    <>
      {priority && (
        <Head>
          <link
            key={`preload-${src}`}
            rel="preload"
            as="image"
            href={src}
            imageSrcSet={srcSet}
            imageSizes={sizes}
            fetchPriority="high"
          />
        </Head>
      )}
      {/* eslint-disable-next-line @next/next/no-img-element -- 원본 CDN 핫링크(저작권 상 로컬 저장 금지), next/image 최적화 대상 아님 */}
      <img
        ref={imgRef}
        src={src}
        srcSet={srcSet}
        sizes={sizes}
        alt={alt}
        width={width}
        height={height}
        loading={priority ? undefined : "lazy"}
        fetchPriority={priority ? "high" : undefined}
        decoding="async"
        onError={onError}
      />
    </>
  );
}
